import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { getUserRole } from '../utils/userPermissions'
import apiClient from '../services/apiClient'
import { userInvoiceService } from '../services/userInvoiceService'
import CardForm from '../components/TransparentCheckout/CardForm'
import PixForm from '../components/TransparentCheckout/PixForm'
import InvoicePaymentLink from '../components/InvoicePaymentLink'
import { logger } from '../utils/logger'
import {
  ArrowLeft, 
  Calendar, 
  CreditCard, 
  User, 
  Mail, 
  FileText, 
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  QrCode,
  Download
} from 'lucide-react'

import { Invoice } from '../types/financial.types'

const InvoiceView: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const userRole = getUserRole(user)
  
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [isLoadingAction, setIsLoadingAction] = useState(false)
  const [showInlinePayment, setShowInlinePayment] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null)
  const [checkoutConfig, setCheckoutConfig] = useState<any>(null)

  useEffect(() => {
    if (id) {
      loadInvoice()
    }
  }, [id])

  const loadInvoice = async () => {
    try {
      setLoading(true)
      // Usar serviço baseado no tipo de usuário
      if (userRole === 'attendant') {
        const response = await userInvoiceService.getInvoiceById(id!)
        setInvoice(response)
      } else {
        const response = await apiClient.get(`/invoices/${id}`)
        setInvoice(response.data.data)
      }
    } catch (error) {
      logger.error('Erro ao carregar fatura:', error)
      setError('Erro ao carregar fatura')
    } finally {
      setLoading(false)
    }
  }

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return { 
          label: 'Pendente', 
          color: 'text-yellow-600 bg-yellow-100', 
          icon: Clock 
        }
      case 'paid':
        return { 
          label: 'Pago', 
          color: 'text-green-600 bg-green-100', 
          icon: CheckCircle 
        }
      case 'cancelled':
        return { 
          label: 'Cancelado', 
          color: 'text-red-600 bg-red-100', 
          icon: XCircle 
        }
      case 'expired':
        return { 
          label: 'Vencido', 
          color: 'text-orange-600 bg-orange-100', 
          icon: AlertCircle 
        }
      default:
        return { 
          label: 'Desconhecido', 
          color: 'text-gray-600 bg-gray-100', 
          icon: AlertCircle 
        }
    }
  }

  const getPaymentMethodInfo = (method: string) => {
    switch (method) {
      case 'pix':
        return { label: 'PIX', icon: QrCode }
      case 'credit_card':
        return { label: 'Cartão de Crédito', icon: CreditCard }
      case 'debit_card':
        return { label: 'Cartão de Débito', icon: CreditCard }
      case 'boleto':
        return { label: 'Boleto Bancário', icon: FileText }
      case 'bank_transfer':
        return { label: 'Transferência Bancária', icon: ExternalLink }
      default:
        return { label: method, icon: CreditCard }
    }
  }

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (error) {
      logger.error('Erro ao copiar:', error)
    }
  }

  const handleCancelInvoice = async () => {
    if (!invoice || !window.confirm('Tem certeza que deseja cancelar esta fatura?')) {
      return
    }

    try {
      setIsLoadingAction(true)
      
      // Usar rota baseada no tipo de usuário
      if (userRole === 'attendant') {
        await apiClient.post(`/user-invoices/${invoice.id}/cancel`)
      } else {
        await apiClient.post(`/invoices/${invoice.id}/cancel`)
      }

      // Recarregar a fatura para atualizar o status
      await loadInvoice()
      setSuccess('Fatura cancelada com sucesso')
    } catch (error) {
      logger.error('Erro ao cancelar fatura:', error)
      setError('Erro ao cancelar fatura')
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handleDownloadPDF = async () => {
    if (!invoice) return

    try {
      setIsLoadingAction(true)
      const response = await apiClient.get(`/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `fatura-${invoice.id}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      logger.error('Erro ao baixar PDF:', error)
      setError('Erro ao gerar PDF da fatura')
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handleInlinePayment = async () => {
    if (!invoice) return

    try {
      setIsLoadingAction(true)
      
      // Criar preferência no MercadoPago
      const response = await apiClient.post(`/invoices/${invoice.id}/create-payment`, {
        payment_method: 'credit_card' // Método padrão
      })
      
      if (response.data.success) {
        const { preference_id, payment_link, qr_code, barcode } = response.data.data
        
        // Configurar checkout transparente
        const config = {
          preferenceId: preference_id,
          amount: invoice.amount || 0,
          currency: invoice.currency,
          payment_link,
          qr_code,
          barcode
        }
        
        setCheckoutConfig(config)
        setShowInlinePayment(true)
        setSelectedPaymentMethod('credit_card')
      }
    } catch (error: any) {
      logger.error('Erro ao iniciar pagamento:', error)
      
      // Tratar erros específicos
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || 'Fatura não pode ser paga no estado atual'
        if (errorMessage.includes('não pode ser paga no estado atual')) {
          setError('Esta fatura já foi paga ou não pode ser paga no momento.')
        } else {
          setError(errorMessage)
        }
      } else {
        setError('Erro ao iniciar pagamento. Tente novamente.')
      }
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handleTransparentPayment = async (cardData: any) => {
    if (!checkoutConfig || !invoice) return

    try {
      setIsLoadingAction(true)
      
      // Processar pagamento transparente
      const response = await apiClient.post(`/invoices/${invoice.id}/transparent-payment`, {
        payment_method: selectedPaymentMethod,
        preference_id: checkoutConfig.preferenceId,
        card_data: cardData
      })
      
      if (response.data.success) {
        setSuccess('Pagamento processado com sucesso!')
        setShowInlinePayment(false)
        loadInvoice() // Recarregar dados da fatura
      } else {
        setError(response.data.error || 'Erro ao processar pagamento')
      }
    } catch (error: any) {
      logger.error('Erro ao processar pagamento transparente:', error)
      setError(error.response?.data?.error || 'Erro ao processar pagamento. Tente novamente.')
    } finally {
      setIsLoadingAction(false)
    }
  }

  const handlePaymentSuccess = () => {
    setSuccess('Pagamento processado com sucesso!')
    setShowInlinePayment(false)
    loadInvoice() // Recarregar dados da fatura
  }

  const handlePaymentFailure = () => {
    setError('Pagamento não foi processado. Tente novamente.')
    setShowInlinePayment(false)
  }

  const formatCurrency = (amount: string, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL' // Usar BRL como padrão se currency for undefined
    }).format(parseFloat(amount))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando fatura...</p>
        </div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Fatura não encontrada</h2>
          <p className="text-gray-600 mb-6">{error || 'A fatura solicitada não existe.'}</p>
          <button
            onClick={() => navigate('/financial')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Voltar para Faturas
          </button>
        </div>
      </div>
    )
  }

  const statusInfo = getStatusInfo(invoice.status)
  const paymentMethodInfo = getPaymentMethodInfo(invoice.payment_method)
  const StatusIcon = statusInfo.icon
  const PaymentIcon = paymentMethodInfo.icon

  return (
    <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Mensagens de Sucesso e Erro */}
          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Sucesso</h3>
                  <p className="text-sm text-green-700 mt-1">{success}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <XCircle className="w-5 h-5 text-red-400 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Erro</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
          <button
            onClick={() => navigate('/financial')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Faturas
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Detalhes da Fatura</h1>
              <p className="text-gray-600 mt-1">ID: {invoice.id}</p>
            </div>
            <div className={`px-4 py-2 rounded-full flex items-center ${statusInfo.color}`}>
              <StatusIcon className="w-4 h-4 mr-2" />
              {statusInfo.label}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações Principais */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dados da Fatura */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações da Fatura</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                  <div className="flex items-center text-2xl font-bold text-gray-900">
                    <DollarSign className="w-6 h-6 mr-2" />
                    {formatCurrency((invoice.amount || 0).toString(), invoice.currency)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pagamento</label>
                  <div className="flex items-center text-gray-900">
                    <PaymentIcon className="w-5 h-5 mr-2" />
                    {paymentMethodInfo.label}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Vencimento</label>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-5 h-5 mr-2" />
                    {formatDate(invoice.due_date)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Criação</label>
                  <div className="flex items-center text-gray-900">
                    <Calendar className="w-5 h-5 mr-2" />
                    {formatDate(invoice.created_at)}
                  </div>
                </div>
                {invoice.paid_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Pagamento</label>
                    <div className="flex items-center text-gray-900">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      {formatDate(invoice.paid_date)}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <p className="text-gray-900">{invoice.description}</p>
              </div>
            </div>

            {/* Dados do Cliente */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Dados do Cliente</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <div className="flex items-center text-gray-900">
                    <User className="w-5 h-5 mr-2" />
                    {invoice.customer_name}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="flex items-center text-gray-900">
                    <Mail className="w-5 h-5 mr-2" />
                    {invoice.customer_email}
                  </div>
                </div>
                {invoice.customer_document && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                    <div className="flex items-center text-gray-900">
                      <FileText className="w-5 h-5 mr-2" />
                      {invoice.customer_document}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Plano de Assinatura */}
            {invoice.subscription_plan_name && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Plano de Assinatura</h2>
                <div className="flex items-center text-gray-900">
                  <FileText className="w-5 h-5 mr-2" />
                  {invoice.subscription_plan_name}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Ações e Pagamento */}
          <div className="space-y-6">
            {/* Status e Ações */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>
              <div className="space-y-3">
                {invoice.status === 'pending' && (
                  <>
                    <button 
                      onClick={() => navigate(`/invoices/${invoice.id}/payment`)}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Efetuar Pagamento
                    </button>
                    <button 
                      onClick={handleCancelInvoice}
                      className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancelar Fatura
                    </button>
                  </>
                )}
                
                {invoice.payment_link && invoice.status === 'pending' && (
                  <button 
                    onClick={handleInlinePayment}
                    disabled={isLoadingAction}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {isLoadingAction ? 'Iniciando Pagamento...' : 'Pagar Agora'}
                  </button>
                )}

                {invoice.status === 'paid' && (
                  <div className="w-full bg-green-50 border border-green-200 text-green-800 py-3 px-4 rounded-lg text-center">
                    <CheckCircle className="w-5 h-5 mx-auto mb-2" />
                    <span className="font-medium">Fatura Paga</span>
                    <p className="text-sm text-green-600 mt-1">
                      Paga em {invoice.paid_date ? new Date(invoice.paid_date).toLocaleDateString('pt-BR') : 'Data não disponível'}
                    </p>
                  </div>
                )}

                {invoice.status === 'cancelled' && (
                  <div className="w-full bg-red-50 border border-red-200 text-red-800 py-3 px-4 rounded-lg text-center">
                    <XCircle className="w-5 h-5 mx-auto mb-2" />
                    <span className="font-medium">Fatura Cancelada</span>
                  </div>
                )}

                {invoice.status === 'expired' && (
                  <div className="w-full bg-orange-50 border border-orange-200 text-orange-800 py-3 px-4 rounded-lg text-center">
                    <AlertCircle className="w-5 h-5 mx-auto mb-2" />
                    <span className="font-medium">Fatura Expirada</span>
                  </div>
                )}

                <button 
                  onClick={handleDownloadPDF}
                  disabled={isLoadingAction}
                  className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isLoadingAction ? 'Gerando PDF...' : 'Baixar PDF'}
                </button>
              </div>
            </div>

            {/* Informações de Pagamento */}
            {(invoice.qr_code || invoice.barcode) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados para Pagamento</h3>
                
                {invoice.qr_code && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">QR Code PIX</label>
                    <div className="bg-gray-100 p-4 rounded-lg text-center">
                      <div className="w-32 h-32 mx-auto bg-white rounded-lg flex items-center justify-center mb-2">
                        <QrCode className="w-16 h-16 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-600">Escaneie com seu app de pagamento</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(invoice.qr_code!, 'qr')}
                      className="mt-2 w-full text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {copied === 'qr' ? 'Copiado!' : 'Copiar Código PIX'}
                    </button>
                  </div>
                )}

                {invoice.barcode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Código de Barras</label>
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <p className="font-mono text-sm text-gray-900 break-all">{invoice.barcode}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(invoice.barcode!, 'barcode')}
                      className="mt-2 w-full text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {copied === 'barcode' ? 'Copiado!' : 'Copiar Código'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Checkout Transparente */}
            {showInlinePayment && checkoutConfig && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Pagamento</h3>
                  <button
                    onClick={() => {
                      setShowInlinePayment(false)
                      setCheckoutConfig(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                {/* Seleção de Método de Pagamento */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Escolha o Método de Pagamento</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => setSelectedPaymentMethod('credit_card')}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        selectedPaymentMethod === 'credit_card'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <CreditCard className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">Cartão de Crédito</span>
                    </button>
                    <button
                      onClick={() => setSelectedPaymentMethod('debit_card')}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        selectedPaymentMethod === 'debit_card'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <CreditCard className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">Cartão de Débito</span>
                    </button>
                    <button
                      onClick={() => setSelectedPaymentMethod('pix')}
                      className={`p-4 border rounded-lg text-center transition-colors ${
                        selectedPaymentMethod === 'pix'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <QrCode className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">PIX</span>
                    </button>
                  </div>
                </div>

                {/* Formulário de Pagamento */}
                {selectedPaymentMethod && (
                  <div className="mb-6">
                    {selectedPaymentMethod === 'pix' && (
                      <PixForm
                        pixCode={checkoutConfig.qr_code || ''}
                        amount={checkoutConfig.amount}
                        currency={checkoutConfig.currency}
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentFailure={handlePaymentFailure}
                      />
                    )}

                    {(selectedPaymentMethod === 'credit_card' || selectedPaymentMethod === 'debit_card') && (
                      <CardForm
                        onPayment={handleTransparentPayment}
                        loading={isLoadingAction}
                        error={error}
                      />
                    )}
                  </div>
                )}

                {/* Botões de Ação */}
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowInlinePayment(false)
                      setCheckoutConfig(null)
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Link de Pagamento para Clientes */}
            {invoice.status === 'pending' && (
              <InvoicePaymentLink
                invoiceId={invoice.id}
                customerName={invoice.customer_name}
                customerEmail={invoice.customer_email}
                amount={parseFloat(invoice.amount.toString())}
                description={invoice.description}
              />
            )}

            {/* Informações Técnicas */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Técnicas</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">ID da Fatura:</span>
                  <span className="font-mono text-gray-900">{invoice.id}</span>
                </div>
                {invoice.external_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID Externo:</span>
                    <span className="font-mono text-gray-900">{invoice.external_id}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Tipo:</span>
                  <span className="text-gray-900 capitalize">{invoice.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Última Atualização:</span>
                  <span className="text-gray-900">{formatDate(invoice.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvoiceView
